import React, { useState, useEffect, useRef } from 'react';
import Button from 'component/atom/forms/button/Button';
import ImagePreview from '../image-preview/ImagePreview';
import FilePreview from '../file-preview/FilePreview';
import DropdownButton from 'component/atom/forms/dropdown-button/DropdownButton';
import Tooltip from 'component/Custom/tooltip/Tooltip';
import AttachmentSvg from '../../../../../assets/icons/vector/attachment.svg';
import SvgWithBranding from 'component/hoc/branding/withBrandingColor.hoc';
import { useBrandingContext } from 'app-context/BrandingsContext';
import styles from './ChatInput.module.scss';
import { useFormikContext } from 'formik';
import toast from 'react-hot-toast';
import useAudioRecorder from './hook/useAudioRecorder';
import { usePost } from 'hooks/usePost';
import { AICall, userEndpoints } from 'hooks/endpoints';
import { API_URL_V2 } from 'constants';
import Loader from 'component/atom/loader/Loader';

const MAX_IMAGE_SIZE_MB = 5;
const MAX_VIDEO_SIZE_MB = 50;
const MAX_DOCUMENT_SIZE_MB = 5;

const ChatInput = ({
  isRequesting,
  capture,
  isSubmitChatLoading,
  forceResetForm,
  hasSidebar,
  settings,
}) => {
  const formik = useFormikContext();
  const fileInputRef = useRef(null);
  const { brandingData } = useBrandingContext();
  const { sideBar_PrimaryColor } = brandingData;
  const [audioBlob, setAudioBlob] = useState(null);
  const { mutate: speechToText, isPending } = usePost({
    onErrorToast: 'Something Went Wrong',
  });
  const { canvasRef, isRecording, startRecording, stopRecording } =
    useAudioRecorder((blob) => {
      if (!blob) return;
      HandleSpeechToTextCall(blob);
    });

  useEffect(() => {
    formik.setFieldValue('Question', '');
  }, [forceResetForm]);

  const blobToBase64 = (blob) => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.readAsDataURL(blob);
      reader.onloadend = () => resolve(reader.result);
      reader.onerror = reject;
    });
  };

  const toggleRecording = () => {
    isRecording ? stopRecording() : startRecording();
  };
  const handleFileUploadClick = () => fileInputRef.current.click();

  const handleFileChange = (event) => {
    const files = Array.from(event.target.files);
    if (files.length === 0) return;

    let newImages = [],
      newVideos = [],
      newDocuments = [],
      rejectedFiles = [];

    files.forEach((file) => {
      const fileObject = { id: URL.createObjectURL(file), file };
      if (file.type.startsWith('image/')) {
        file.size <= MAX_IMAGE_SIZE_MB * 1024 * 1024
          ? newImages.push(fileObject)
          : rejectedFiles.push(file);
      } else if (file.type.startsWith('video/')) {
        file.size <= MAX_VIDEO_SIZE_MB * 1024 * 1024
          ? newVideos.push(fileObject)
          : rejectedFiles.push(file);
      } else {
        file.size <= MAX_DOCUMENT_SIZE_MB * 1024 * 1024
          ? newDocuments.push(fileObject)
          : rejectedFiles.push(file);
      }
    });

    if (rejectedFiles.length > 0)
      toast.error('Some files exceeded the size limit and were not uploaded.');

    formik.setFieldValue('images', newImages);
    formik.setFieldValue('files', [...newVideos, ...newDocuments]);
  };

  const removeImage = (id) =>
    formik.setFieldValue(
      'images',
      formik.values.images.filter((img) => img.id !== id)
    );
  const removeFile = (id) =>
    formik.setFieldValue(
      'files',
      formik.values.files.filter((file) => file.id !== id)
    );

  const attachmentDrop = [
    { label: 'Upload file', function: handleFileUploadClick },
    { label: 'Upload Image', function: handleFileUploadClick },
  ];
  const handleDeleteRecording = () => {
    setAudioBlob(null);

    // 🧹 Clear the waveform from canvas
    if (canvasRef.current) {
      const ctx = canvasRef.current.getContext('2d');
      ctx.clearRect(0, 0, canvasRef.current.width, canvasRef.current.height);
    }
  };
  const HandleSpeechToTextCall = async (audioBlob) => {
    const fileName = `audio-${Date.now()}.webm`;
    const contentType = 'audio/webm';

    const file = new File([audioBlob], fileName, { type: contentType });

    const formData = new FormData();
    formData.append('fileData', file);

    handleDeleteRecording();

    speechToText(
      {
        url: AICall.speechToText,
        data: formData,
        requireAuth: true,
        baseUrl: API_URL_V2,
      },
      {
        onSuccess: async (response) => {
          const transcript = response?.data?.text || response?.text || '';
          const prevText = formik.values.Question || '';
          formik.setFieldValue('Question', `${prevText} ${transcript}`.trim());
        },
        onError: (error) => {
          console.error('response error:', error);
        },
      }
    );
  };

  return (
    <div className={styles['chat-control']}>
      <div className={styles['chat-input']}>
        <input
          type='file'
          ref={fileInputRef}
          style={{ display: 'none' }}
          onChange={handleFileChange}
          multiple
        />

        {(formik.values.images.length > 0 ||
          formik.values.files.length > 0) && (
          <div className={styles['upload-container']}>
            {formik.values.images.map((img) => (
              <ImagePreview
                key={img.id}
                src={img.id}
                handleChange={() => removeImage(img.id)}
              />
            ))}
            {formik.values.files.map((file) => (
              <FilePreview
                key={file.id}
                file={file}
                handleChange={() => removeFile(file.id)}
              />
            ))}
          </div>
        )}
        {!isRecording && (
          <textarea
            key={formik}
            name='Question'
            value={formik.values.Question}
            onChange={isSubmitChatLoading ? () => {} : formik.handleChange}
            placeholder='Message AiDucator'
            onKeyDown={(e) => {
              if (!isRequesting && e.key === 'Enter') {
                e.preventDefault();
                formik.handleSubmit();
              }
            }}
            disabled={!capture?.text}
            className={styles['chat-input-textarea']}
          />
        )}

        <div
          className={`${styles['wave-container']} ${
            !isRecording ? styles['not-recording'] : ''
          }`}
        >
          <canvas
            ref={canvasRef}
            width={400}
            height={60}
            className={styles.waveform}
          />
        </div>
        {/* {isRecording && <p className={styles['recording']}>Recording...</p>} */}
        {/* {isRecording && ( */}

        {/* )} */}

        {/* {audioBlob && !isRecording && (
            <>
              <Button
                type={isPlaying ? 'pause' : 'play'}
                className={styles['audio-button']}
                svgClassName={styles.svg}
                onClick={togglePlayPause}
              />
              <p>
                {isPlaying ? formatTime(currentTime) : formatTime(duration)}
              </p>
              {!isPlaying && (
                <Button
                  type="delete"
                  className={styles['audio-button']}
                  svgClassName={styles.svg}
                  onClick={deleteRecording}
                  variant="#ff0000"
                />
              )}
            </>
          )} */}

        <div className={styles['chat-input-actions']}>
          <div className={styles['action']}>
            {isRecording ? (
              <div
                className={styles['rec-state']}
                onClick={toggleRecording}
              ></div>
            ) : !isPending ? (
              <Tooltip content='Record Audio'>
                <SvgWithBranding
                  type='record'
                  color={isRecording ? 'red' : '#7E7E7E'}
                  onClick={toggleRecording}
                />
              </Tooltip>
            ) : (
              <Loader style={{ width: '20px', height: '20px' }} />
            )}

            {capture?.attachment && (
              <Tooltip content='Attach File'>
                <DropdownButton
                  src={AttachmentSvg}
                  items={attachmentDrop}
                  type='attachments'
                  position='top'
                  disabled={!capture?.attachment}
                />
              </Tooltip>
            )}
          </div>

          <div className={styles['action']}>
            <SvgWithBranding
              type='send_vector'
              // className={styles.button}
              // svgClassName={styles.svg}
              color='#7E7E7E'
              onClick={formik.handleSubmit}
              disabled={
                isRequesting ||
                Object.values(formik.values).every((value) => !value) ||
                !capture?.text
              }
              // isLoading={isPending}
            />
          </div>
        </div>
      </div>
    </div>
  );
};

export default ChatInput;
